import { useState, useEffect, useRef } from "react";
import { http, resolveImage } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Search, Package, DollarSign, User, MapPin, CreditCard,
  Check, X, Loader2, ArrowLeft, Banknote, Smartphone, Hash,
  Palette, ShoppingBag
} from "lucide-react";

const C = {
  bg: "#070707", bg2: "#11100E", card: "#181614", text: "#F5F2EB", text2: "#B5B0A7",
  gold: "#C9A055", red: "#FF3B30", green: "#4ade80", purple: "#7C3AED",
  bSub: "rgba(245,242,235,0.10)", bGold: "rgba(201,160,85,0.35)",
};
const F = { cond: "'Saira Condensed', sans-serif", sans: "'DM Sans', 'Outfit', sans-serif", brush: "'Permanent Marker', cursive" };

const PAYMENT_METHODS = [
  { key: "cash", label: "Cash", icon: Banknote, color: "#4ade80" },
  { key: "venmo", label: "Venmo", icon: Smartphone, color: "#008CFF" },
  { key: "cashapp", label: "Cash App", icon: DollarSign, color: "#00D632" },
  { key: "card", label: "Card", icon: CreditCard, color: "#C9A055" },
  { key: "zelle", label: "Zelle", icon: Smartphone, color: "#6D1ED4" },
];

export default function BoothSalesMode() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [boothTab, setBoothTab] = useState("drawings"); // "drawings" | "merch"
  const [artworks, setArtworks] = useState([]);
  const [merchProducts, setMerchProducts] = useState([]);
  const [recentSales, setRecentSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  // Sale form
  const [selectedItem, setSelectedItem] = useState(null);
  const [isMerchSale, setIsMerchSale] = useState(false);
  const [qty, setQty] = useState(1);
  const [customerName, setCustomerName] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [lastSale, setLastSale] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== "admin") { navigate("/login"); return; }
    loadData();
  }, [user, authLoading, navigate]);

  const loadData = async () => {
    try {
      const [aRes, mRes, rRes] = await Promise.all([
        http.get("/marker-collection/admin/artworks"),
        http.get("/marker-collection/admin/merch/products"),
        http.get("/marker-collection/admin/booth-sale/recent"),
      ]);
      setArtworks((aRes.data || []).filter(a => a.status === "active"));
      setMerchProducts((mRes.data || []).filter(p => p.status === "active"));
      setRecentSales(rRes.data || []);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };

  const currentItems = boothTab === "drawings" ? artworks : merchProducts;
  const filteredItems = search.trim()
    ? currentItems.filter(item => {
        const q = search.toLowerCase();
        return (item.title || "").toLowerCase().includes(q) ||
          (item.vendor_code || "").toLowerCase().includes(q) ||
          (item.art_type || item.category_name || "").toLowerCase().includes(q);
      })
    : currentItems;

  const selectItem = (item, isMerch) => {
    setSelectedItem(item);
    setIsMerchSale(isMerch);
    setQty(1);
    setSearch("");
  };

  const resetForm = () => {
    setSelectedItem(null);
    setIsMerchSale(false);
    setQty(1);
    setCustomerName("");
    setCity("");
    setState("");
    setPaymentMethod("cash");
    setNotes("");
    setLastSale(null);
  };

  const handleSale = async () => {
    if (!selectedItem) return;
    setSubmitting(true);
    try {
      const endpoint = isMerchSale
        ? "/marker-collection/admin/merch/booth-sale"
        : "/marker-collection/admin/booth-sale";
      const payload = {
        [isMerchSale ? "product_id" : "artwork_id"]: selectedItem.id,
        quantity: qty,
        customer_name: customerName || "Walk-in",
        city, state,
        payment_method: paymentMethod,
        notes,
      };
      const { data } = await http.post(endpoint, payload);
      setLastSale(data);
      toast.success(`Sale recorded! ${data.title} — $${data.total.toFixed(2)}`);
      loadData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sale failed");
    } finally { setSubmitting(false); }
  };

  if (authLoading || loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: C.bg }}>
      <div className="w-16 h-16 border-2 animate-spin" style={{ borderColor: `${C.gold}30`, borderTopColor: C.gold }} />
    </div>
  );

  const total = selectedItem ? Number(selectedItem.price || 0) * qty : 0;

  // ─── Sale Success Screen ───
  if (lastSale) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6" style={{ background: C.bg, color: C.text }} data-testid="booth-sale-success">
        <div className="w-20 h-20 rounded-full flex items-center justify-center mb-6 animate-bounce" style={{ background: `${C.green}20`, border: `3px solid ${C.green}` }}>
          <Check size={36} style={{ color: C.green }} />
        </div>
        <h1 className="text-4xl font-black tracking-tight mb-2" style={{ fontFamily: F.cond, color: C.green }}>SALE RECORDED</h1>
        <p className="text-xl font-bold mb-1" style={{ fontFamily: F.cond, color: C.text }}>{lastSale.title}</p>
        <p className="text-sm mb-4" style={{ fontFamily: F.sans, color: C.text2 }}>
          {lastSale.vendor_code} &middot; {lastSale.quantity}x &middot; ${lastSale.total.toFixed(2)}
        </p>
        {lastSale.edition_numbers?.length > 0 && (
          <p className="text-xs mb-4" style={{ fontFamily: F.sans, color: C.gold }}>
            Edition #{lastSale.edition_numbers.join(", #")} assigned
          </p>
        )}
        {lastSale.remaining_stock != null && (
          <p className="text-xs mb-6" style={{ fontFamily: F.sans, color: C.text2 }}>
            {lastSale.remaining_stock} remaining in stock
          </p>
        )}
        <button onClick={resetForm}
          className="px-8 py-4 text-sm tracking-[0.2em] uppercase font-bold"
          style={{ fontFamily: F.sans, background: C.gold, color: C.bg }}
          data-testid="booth-next-sale">
          NEXT SALE
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: C.bg, color: C.text }}>
      {/* Header */}
      <header className="sticky top-0 z-30 px-4 py-3 flex items-center justify-between"
        style={{ background: C.bg2, borderBottom: `1px solid ${C.bSub}`, backdropFilter: "blur(12px)" }}
        data-testid="booth-header">
        <div className="flex items-center gap-3">
          <button onClick={() => window.close()} className="p-1.5" style={{ color: C.text2 }}>
            <X size={20} />
          </button>
          <div>
            <h1 className="text-lg font-black tracking-tight leading-none" style={{ fontFamily: F.cond, color: C.gold }}>BOOTH MODE</h1>
            <p className="text-[9px] tracking-[0.25em] uppercase" style={{ fontFamily: F.sans, color: C.text2 }}>THE MARKER COLLECTION</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-xl font-black" style={{ fontFamily: F.cond, color: C.text }}>{recentSales.length}</p>
          <p className="text-[8px] tracking-[0.2em] uppercase" style={{ fontFamily: F.sans, color: C.text2 }}>SALES TODAY</p>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 py-5">
        {!selectedItem ? (
          <>
            {/* Tab Toggle */}
            <div className="flex mb-4 border" style={{ borderColor: C.bSub }} data-testid="booth-tabs">
              <button onClick={() => { setBoothTab("drawings"); setSearch(""); }}
                className="flex-1 py-3 flex items-center justify-center gap-2 text-[10px] tracking-[0.15em] uppercase font-bold transition-colors"
                style={{
                  fontFamily: F.sans,
                  background: boothTab === "drawings" ? C.gold : "transparent",
                  color: boothTab === "drawings" ? C.bg : C.text2,
                }}
                data-testid="booth-tab-drawings">
                <Palette size={14} /> Drawings ({artworks.length})
              </button>
              <button onClick={() => { setBoothTab("merch"); setSearch(""); }}
                className="flex-1 py-3 flex items-center justify-center gap-2 text-[10px] tracking-[0.15em] uppercase font-bold transition-colors"
                style={{
                  fontFamily: F.sans,
                  background: boothTab === "merch" ? C.purple : "transparent",
                  color: boothTab === "merch" ? "#fff" : C.text2,
                }}
                data-testid="booth-tab-merch">
                <ShoppingBag size={14} /> Merch ({merchProducts.length})
              </button>
            </div>

            {/* Search */}
            <div className="relative mb-4" data-testid="booth-search-box">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: C.text2 }} />
              <input value={search} onChange={e => setSearch(e.target.value)} autoFocus
                className="w-full pl-10 pr-4 py-3.5 text-base outline-none"
                style={{ fontFamily: F.sans, background: C.card, border: `2px solid ${boothTab === "merch" ? C.purple + "60" : C.bGold}`, color: C.text }}
                placeholder={boothTab === "drawings" ? "Search by name or vendor code..." : "Search merch..."}
                data-testid="booth-search-input" />
            </div>

            {/* Item Grid */}
            <div className="grid grid-cols-2 gap-3" data-testid="booth-item-grid">
              {filteredItems.map(item => {
                const outOfStock = item.quantity != null && item.quantity <= 0;
                const isMerch = boothTab === "merch";
                const accentColor = isMerch ? C.purple : C.gold;
                return (
                  <button key={item.id} onClick={() => !outOfStock && selectItem(item, isMerch)} disabled={outOfStock}
                    className="text-left p-0 border transition-all active:scale-95 disabled:opacity-40"
                    style={{ background: C.card, borderColor: C.bSub }}
                    data-testid={`booth-item-${item.id}`}>
                    <div className="relative">
                      {item.main_image ? (
                        <img src={resolveImage(item.main_image)} alt="" className="w-full aspect-square object-cover" loading="lazy" />
                      ) : (
                        <div className="w-full aspect-square flex items-center justify-center" style={{ background: C.bg2 }}>
                          {isMerch ? <ShoppingBag size={24} style={{ color: `${accentColor}20` }} /> : <Package size={24} style={{ color: `${accentColor}20` }} />}
                        </div>
                      )}
                      <div className="absolute top-1.5 left-1.5 px-2 py-0.5 text-[9px] font-bold font-mono"
                        style={{ background: "rgba(0,0,0,0.8)", color: accentColor, backdropFilter: "blur(4px)" }}>
                        {item.vendor_code || "—"}
                      </div>
                      <div className="absolute bottom-1.5 right-1.5 px-2 py-0.5"
                        style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(4px)" }}>
                        <span className="text-base font-black" style={{ fontFamily: F.cond, color: accentColor }}>${Number(item.price || 0).toFixed(0)}</span>
                      </div>
                      {outOfStock && (
                        <div className="absolute inset-0 flex items-center justify-center" style={{ background: "rgba(0,0,0,0.7)" }}>
                          <span className="text-xs tracking-[0.3em] uppercase font-black" style={{ fontFamily: F.cond, color: C.text }}>SOLD OUT</span>
                        </div>
                      )}
                    </div>
                    <div className="p-2">
                      <p className="text-xs font-bold truncate" style={{ fontFamily: F.sans, color: C.text }}>{item.title}</p>
                      <p className="text-[9px]" style={{ fontFamily: F.sans, color: C.text2 }}>
                        {isMerch ? item.category_name : item.art_type} {item.quantity != null ? `· ${item.quantity} left` : ""}
                      </p>
                    </div>
                  </button>
                );
              })}
              {filteredItems.length === 0 && (
                <div className="col-span-2 text-center py-12">
                  <Package size={32} className="mx-auto mb-3" style={{ color: `${C.gold}30` }} />
                  <p className="text-sm" style={{ fontFamily: F.sans, color: C.text2 }}>
                    {search ? `No results for "${search}"` : `No active ${boothTab === "merch" ? "products" : "artworks"}`}
                  </p>
                </div>
              )}
            </div>
          </>
        ) : (
          /* ─── Sale Form ─── */
          <div data-testid="booth-sale-form">
            <button onClick={() => setSelectedItem(null)} className="w-full flex items-center gap-3 p-3 mb-5 text-left"
              style={{ background: C.card, border: `2px solid ${isMerchSale ? C.purple + "60" : C.bGold}` }} data-testid="booth-selected-item">
              <ArrowLeft size={16} style={{ color: isMerchSale ? C.purple : C.gold }} />
              {selectedItem.main_image && (
                <img src={resolveImage(selectedItem.main_image)} alt="" className="w-12 h-12 object-cover flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold truncate" style={{ fontFamily: F.sans, color: C.text }}>{selectedItem.title}</p>
                <p className="text-[10px]" style={{ fontFamily: F.sans, color: C.text2 }}>
                  {selectedItem.vendor_code} · {isMerchSale ? selectedItem.category_name : selectedItem.art_type}
                </p>
              </div>
              <span className="text-xl font-black flex-shrink-0" style={{ fontFamily: F.cond, color: isMerchSale ? C.purple : C.gold }}>
                ${Number(selectedItem.price || 0).toFixed(0)}
              </span>
            </button>

            {/* Quantity */}
            <div className="mb-4">
              <label className="text-[9px] tracking-[0.3em] uppercase block mb-1.5" style={{ fontFamily: F.sans, color: `${C.gold}90` }}>QUANTITY</label>
              <div className="flex items-center gap-3">
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-12 h-12 flex items-center justify-center text-xl font-bold"
                  style={{ background: C.card, border: `1px solid ${C.bSub}`, color: C.text }} data-testid="booth-qty-minus">−</button>
                <span className="text-3xl font-black min-w-[50px] text-center" style={{ fontFamily: F.cond, color: C.text }} data-testid="booth-qty-display">{qty}</span>
                <button onClick={() => setQty(qty + 1)} className="w-12 h-12 flex items-center justify-center text-xl font-bold"
                  style={{ background: C.card, border: `1px solid ${C.bSub}`, color: C.text }} data-testid="booth-qty-plus">+</button>
                <div className="flex-1 text-right">
                  <span className="text-3xl font-black" style={{ fontFamily: F.cond, color: isMerchSale ? C.purple : C.gold }} data-testid="booth-total">${total.toFixed(0)}</span>
                </div>
              </div>
            </div>

            {/* Payment Method */}
            <div className="mb-4">
              <label className="text-[9px] tracking-[0.3em] uppercase block mb-1.5" style={{ fontFamily: F.sans, color: `${C.gold}90` }}>PAYMENT</label>
              <div className="grid grid-cols-5 gap-2" data-testid="booth-payment-methods">
                {PAYMENT_METHODS.map(pm => {
                  const active = paymentMethod === pm.key;
                  return (
                    <button key={pm.key} onClick={() => setPaymentMethod(pm.key)}
                      className="flex flex-col items-center gap-1 py-3 transition-all active:scale-95"
                      style={{ background: active ? `${pm.color}15` : C.card, border: `2px solid ${active ? pm.color : C.bSub}` }}
                      data-testid={`booth-pay-${pm.key}`}>
                      <pm.icon size={18} style={{ color: active ? pm.color : C.text2 }} />
                      <span className="text-[8px] tracking-[0.15em] uppercase font-bold" style={{ fontFamily: F.sans, color: active ? pm.color : C.text2 }}>{pm.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Customer Info */}
            <div className="mb-4 space-y-3">
              <div>
                <label className="text-[9px] tracking-[0.3em] uppercase block mb-1.5" style={{ fontFamily: F.sans, color: `${C.gold}90` }}>CUSTOMER NAME</label>
                <div className="relative">
                  <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: C.text2 }} />
                  <input value={customerName} onChange={e => setCustomerName(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 text-sm outline-none"
                    style={{ fontFamily: F.sans, background: C.card, border: `1px solid ${C.bSub}`, color: C.text }}
                    placeholder="Walk-in (optional)" data-testid="booth-customer-name" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[9px] tracking-[0.3em] uppercase block mb-1.5" style={{ fontFamily: F.sans, color: `${C.gold}90` }}>CITY</label>
                  <input value={city} onChange={e => setCity(e.target.value)}
                    className="w-full px-3 py-3 text-sm outline-none"
                    style={{ fontFamily: F.sans, background: C.card, border: `1px solid ${C.bSub}`, color: C.text }}
                    placeholder="City" data-testid="booth-city" />
                </div>
                <div>
                  <label className="text-[9px] tracking-[0.3em] uppercase block mb-1.5" style={{ fontFamily: F.sans, color: `${C.gold}90` }}>STATE</label>
                  <input value={state} onChange={e => setState(e.target.value)}
                    className="w-full px-3 py-3 text-sm outline-none"
                    style={{ fontFamily: F.sans, background: C.card, border: `1px solid ${C.bSub}`, color: C.text }}
                    placeholder="TN" maxLength={2} data-testid="booth-state" />
                </div>
              </div>
            </div>

            {/* Complete Sale */}
            <button onClick={handleSale} disabled={submitting}
              className="w-full py-4 flex items-center justify-center gap-3 text-sm tracking-[0.2em] uppercase font-bold active:scale-[0.98] disabled:opacity-50 transition-all"
              style={{ fontFamily: F.sans, background: C.green, color: C.bg }}
              data-testid="booth-complete-sale">
              {submitting ? <Loader2 size={18} className="animate-spin" /> : <Check size={18} />}
              {submitting ? "PROCESSING..." : `COMPLETE SALE · $${total.toFixed(0)}`}
            </button>
          </div>
        )}

        {/* Recent Sales */}
        {!selectedItem && recentSales.length > 0 && (
          <div className="mt-8" data-testid="booth-recent-sales">
            <h3 className="text-[10px] tracking-[0.25em] uppercase mb-3" style={{ fontFamily: F.sans, color: C.gold }}>RECENT BOOTH SALES</h3>
            <div className="space-y-1.5">
              {recentSales.slice(0, 10).map(s => (
                <div key={s.id} className="flex items-center gap-3 px-3 py-2.5" style={{ background: C.card, border: `1px solid ${C.bSub}` }}>
                  <Check size={14} style={{ color: s.merch_sale ? C.purple : C.green }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs truncate" style={{ fontFamily: F.sans, color: C.text }}>{s.artwork_title}</p>
                    <p className="text-[9px]" style={{ fontFamily: F.sans, color: C.text2 }}>
                      {s.customer_name} · {s.quantity}x {s.merch_sale ? "· Merch" : ""}
                    </p>
                  </div>
                  <span className="text-sm font-black flex-shrink-0" style={{ fontFamily: F.cond, color: s.merch_sale ? C.purple : C.gold }}>${Number(s.price || 0).toFixed(0)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
